import React, {useContext, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import SpeechBubble from '~/src/components/svg/action/SpeechBubble';
import Notification from '~/src/components/notification/Notification';
import Share from '~/src/components/svg/action/Share';
import ShareModal from '~/src/components/share/shareModal/ShareModal';
import Modal from '~/src/components/modal/Modal';
import ThumbsUpDown from '~/src/components/svg/action/ThumbsUpDown';

import AppContext from '~/src/components/context/Context';
import {TypeContext} from '~/src/components/context/TypeContext';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {SitemapContext} from '~/src/components/context/SitemapContext';
import {SectionContext} from '~/src/components/context/SectionContext';
import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {Feature} from '~/src/utilities/constants/feature';
import {getContentUrl} from '~/src/utilities/helpers/link';
import {Hashes} from '~/src/utilities/constants/hash.js';
import {pushRoute} from '~/src/utilities/helpers/router';
import {trackGtm} from '~/src/utilities/tracking/gtm';
import {GtmEvents, InteractionSources} from '~/src/utilities/constants/tracking';
import {ColorScheme, TeaserFormat} from '~/src/utilities/constants/formats';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {ContentStyle} from '~/src/utilities/constants/style';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './TeaserAction.module.scss';
import i18nStrings from './TeaserAction.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const TeaserAction = (props) => {
    useContext(AppContext);
    const sitemap = useContext(SitemapContext);
    const type = useContext(TypeContext);
    const settings = useContext(SettingsContext);
    const section = useContext(SectionContext);

    const [state, setState] = useState({
        showNotification: false,
        showShareModal: false,
        message: null
    });

    const closeModal = useCallback(() => {
        setState({...state, showNotification: false, showShareModal: false});
    }, []);

    const showNotification = useCallback((message) => {
        setState({...state, showNotification: true, message});
    }, []);

    const showShareModal = useCallback(() => {
        setState({...state, showShareModal: true});
    }, []);

    const handleRatingClick = useCallback(
        (event) => {
            event.preventDefault();

            trackGtm(GtmEvents.INTERACTION, {
                interactionType: 'rating',
                interactionSource: InteractionSources.QUICKACTION_TEASER,
                contentId: props.id
            });

            if (window.location.pathname.endsWith(props.id)) {
                pushRoute(sitemap.all, getContentUrl(window.location.pathname, Hashes.RATING_SECTION), true);
            } else {
                pushRoute(sitemap.all, getContentUrl(`${props.id}`, Hashes.RATING_SECTION));
            }

            props.onRatingClick && props.onRatingClick();
        },
        [props.id, sitemap.all]
    );

    const handleCommentClick = useCallback(
        (event) => {
            event.preventDefault();

            if (get(props, 'settings.commentingEnabled')) {
                if (props.openCommentPage) {
                    pushRoute(sitemap.all, getContentUrl(`comment/${props.id}`));
                } else {
                    pushRoute(sitemap.all, getContentUrl(`${props.id}`, Hashes.COMMENT_SECTION));
                }
            } else {
                showNotification(getTranslation('commentingDisabledMessage'));
            }

            trackGtm(GtmEvents.INTERACTION, {
                interactionType: 'comment',
                interactionSource: InteractionSources.QUICKACTION_TEASER,
                contentId: props.id
            });
        },
        [props.id, props.settings, sitemap.all]
    );

    const handleShareClick = useCallback(
        (event) => {
            event.preventDefault();

            if (props.isDisabled) {
                showNotification(getTranslation('sharingDisabledMessage'));
            } else {
                showShareModal();
            }

            trackGtm(GtmEvents.INTERACTION, {
                interactionType: 'share',
                interactionSource: InteractionSources.QUICKACTION_TEASER,
                contentId: props.id
            });
        },
        [props.id, props.isDisabled]
    );

    const isLifestyleSection = section.layout === SectionLayout.LIFESTYLE || get(props, 'styles.name') === ContentStyle.LIFESTYLE;
    const teaserActionClass = classNames(css, 'teaserAction', type, settings.siteArea, getFormatClassNames(props.format), {isLifestyleSection});

    const showComments = isEnabled(Feature.COMMENTS) && get(props, 'settings.commentingEnabled', false);
    const likesCount = parseInt(props.likes) || 0;
    const ratingsCount = parseInt(props.contentRatings) || likesCount;
    const commentsCount = parseInt(props.comments) || 0;
    const sharesCount = parseInt(props.shares) || 0;

    const ratingClass = classNames(css, 'ratings', 'icon');
    const commentClass = classNames(css, 'comments', 'icon');
    const shareClass = classNames(css, 'shares', 'icon');

    return (
        <div className={teaserActionClass} data-testid="TeaserAction">
            <button className={ratingClass} onClick={handleRatingClick} data-testid="TeaserActionRating">
                <ThumbsUpDown
                    title={getTranslation('likeTitle')}
                    primaryClassName={css.primaryColor}
                    secondaryClassName={css.secondaryColor}
                    tertiaryClassName={css.tertiaryColor}
                />
                {props.showNumbers && ratingsCount}
            </button>
            {showComments && (
                <button className={commentClass} onClick={handleCommentClick} data-testid="TeaserActionComment">
                    <SpeechBubble
                        title={getTranslation('commentTitle')}
                        primaryClassName={css.primaryColor}
                        secondaryClassName={css.secondaryColor}
                        tertiaryClassName={css.tertiaryColor}
                    />
                    {props.showNumbers && commentsCount}
                </button>
            )}
            {isEnabled(Feature.SHARE) && (
                <button className={shareClass} onClick={handleShareClick} data-testid="TeaserActionShare">
                    <Share
                        title={getTranslation('shareTitle')}
                        primaryClassName={css.primaryColor}
                        secondaryClassName={css.secondaryColor}
                        tertiaryClassName={css.tertiaryColor}
                    />
                    {props.showNumbers && sharesCount}
                </button>
            )}
            {state.showNotification && (
                <Modal>
                    <Notification onClose={closeModal} message={state.message} />
                </Modal>
            )}
            {state.showShareModal && (
                <Modal>
                    <ShareModal onClose={closeModal} metadataId={props.id} />
                </Modal>
            )}
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    TeaserAction.propTypes = {
        settings: PropTypes.shape({
            commentingEnabled: PropTypes.bool.isRequired
        }),
        id: PropTypes.string.isRequired,
        type: PropTypes.string,
        onRatingClick: PropTypes.func,
        showNumbers: PropTypes.bool,
        format: PropTypes.shape({
            primaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            secondaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            colorScheme: PropTypes.oneOf(Object.values(ColorScheme)).isRequired
        }),
        styles: PropTypes.shape({
            name: PropTypes.string
        })
    };
}

export default TeaserAction;
